import { FormEvent, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Radio,
  RefreshCw,
  Loader2,
  Plus,
  Trash2,
  Pencil,
  Send,
  Link2,
  AlertTriangle,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import { useTabSearchParam } from '@/client/hooks/useTabSearchParam';
import { getApiErrorMessage } from '@/client/utils/errors';
import { generateIdempotencyKey } from '@/client/utils/requestSecurity';
import {
  normalizeProviderTemplate,
  useNotificationProvidersQuery,
  useProviderEventsQuery,
  useProviderSendersQuery,
  useProviderTemplateQuery,
  useProviderTemplatesQuery,
  useProvidersHealthQuery,
} from '@/client/api/notifications-infra/notifications.query';
import {
  useCreateProviderTemplateMutation,
  useDeleteProviderTemplateMutation,
  useTestProviderTemplateMutation,
  useUpdateProviderTemplateMutation,
} from '@/client/api/notifications-infra/notifications.mutation';
import {
  CreateProviderTemplatePayload,
  ProviderEventName,
  ProviderSender,
  ProviderTemplate,
} from '@/client/types/notifications-infra';
import MapLocalTemplateDialog from '@/client/components/admin/NotificationsInfra/MapLocalTemplateDialog';
import {
  CapabilityChips,
  DELIVERY_BASE,
  DeliveryAccessDenied,
  DeliveryPageShell,
  HealthStatus,
  describeProviderError,
  formatChannel,
  formatProviderName,
  hasCapability,
  providerKey,
  resolveHealth,
} from '@/client/components/admin/NotificationsInfra/deliveryUi';

const EVENT_NAMES: ProviderEventName[] = [
  'delivered',
  'bounced',
  'blocked',
  'spam',
  'deferred',
];

type Tab = 'templates' | 'senders' | 'events';

const emptyForm = (): CreateProviderTemplatePayload => ({
  name: '',
  subject: '',
  sender: { name: '', email: '' },
  html_content: '',
  is_active: true,
  reply_to: '',
  tag: '',
});

const formFromTemplate = (tpl: ProviderTemplate): CreateProviderTemplatePayload => {
  const n = normalizeProviderTemplate(tpl, tpl.id);
  return {
    name: n.name ?? '',
    subject: n.subject ?? '',
    sender: {
      name: n.sender?.name ?? '',
      email: n.sender?.email ?? '',
    },
    html_content: n.html_content ?? '',
    is_active: n.is_active !== false && n.is_active !== 0,
    reply_to: n.reply_to ?? '',
    tag: n.tag ?? '',
  };
};

const isVerified = (s: ProviderSender) => s.verified === true || s.verified === 1;

const DeliveryProviderDetailPage = () => {
  const { provider: providerParam = '' } = useParams<{ provider: string }>();
  const provider = decodeURIComponent(providerParam);

  useBreadcrumbs([
    { label: 'Communications', href: `${DELIVERY_BASE}/delivery` },
    { label: 'Notifications', href: `${DELIVERY_BASE}/delivery` },
    { label: 'Providers', href: `${DELIVERY_BASE}/providers` },
    { label: formatProviderName(provider) || 'Provider' },
  ]);

  const viewPerm = useHasPermission('messaging.view');
  const writePerm = useHasPermission('messaging.template');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canWrite = writePerm.isSuperAdmin || writePerm.hasPermission;
  const permLoading = viewPerm.isLoading;

  const providersQ = useNotificationProvidersQuery(undefined, canView);
  const row = (providersQ.data?.providers ?? []).find((p) => providerKey(p) === provider);
  const healthQ = useProvidersHealthQuery(provider, canView && !!provider);

  const caps = row?.capabilities ?? [];
  const availableTabs = useMemo(() => {
    const tabs: Tab[] = [];
    if (!row || hasCapability(row, 'templates')) tabs.push('templates');
    if (!row || hasCapability(row, 'senders')) tabs.push('senders');
    if (!row || hasCapability(row, 'events')) tabs.push('events');
    return tabs.length ? tabs : (['templates'] as Tab[]);
  }, [row]);

  const [tab, setTab] = useTabSearchParam<Tab>(
    availableTabs.length ? availableTabs : (['templates'] as const),
    availableTabs[0] ?? 'templates',
  );
  const activeTab = availableTabs.includes(tab) ? tab : availableTabs[0] ?? 'templates';

  const canTemplates = !row || hasCapability(row, 'templates');
  const canSenders = !row || hasCapability(row, 'senders');
  const canEvents = !row || hasCapability(row, 'events');
  const canTest = !row || hasCapability(row, 'test_send');
  const templateIdColumn = row?.template_id_column ?? null;

  const templatesQ = useProviderTemplatesQuery(provider, canView && canTemplates);
  const sendersQ = useProviderSendersQuery(provider, canView && canSenders);

  const [eventEmail, setEventEmail] = useState('');
  const [eventName, setEventName] = useState('');
  const [eventTemplateId, setEventTemplateId] = useState('');
  const [eventStart, setEventStart] = useState('');
  const [eventEnd, setEventEnd] = useState('');
  const eventsQ = useProviderEventsQuery(
    provider,
    {
      ...(eventEmail.trim() ? { email: eventEmail.trim() } : {}),
      ...(eventName ? { event: eventName } : {}),
      ...(eventTemplateId.trim() ? { template_id: eventTemplateId.trim() } : {}),
      ...(eventStart ? { start_date: eventStart } : {}),
      ...(eventEnd ? { end_date: eventEnd } : {}),
    },
    canView && canEvents && activeTab === 'events',
  );

  const [editorOpen, setEditorOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [form, setForm] = useState<CreateProviderTemplatePayload>(emptyForm);
  const [showPreview, setShowPreview] = useState(false);

  const [testOpen, setTestOpen] = useState(false);
  const [testId, setTestId] = useState<string | number | null>(null);
  const [testEmails, setTestEmails] = useState('');

  const [mapOpen, setMapOpen] = useState(false);
  const [mapId, setMapId] = useState<string | number | null>(null);
  const [postCreate, setPostCreate] = useState<{ id: string | number; name?: string } | null>(null);

  const detailQ = useProviderTemplateQuery(
    provider,
    editingId != null ? String(editingId) : undefined,
    editorOpen && editingId != null,
  );

  useEffect(() => {
    if (!editorOpen || editingId == null || !detailQ.data) return;
    const next = formFromTemplate(detailQ.data);
    setForm((prev) => ({
      ...next,
      // Keep whatever the user already typed if detail omitted a field.
      name: next.name || prev.name,
      subject: next.subject || prev.subject,
      sender: {
        name: next.sender.name || prev.sender.name,
        email: next.sender.email || prev.sender.email,
      },
      html_content: next.html_content || prev.html_content,
      reply_to: next.reply_to || prev.reply_to,
      tag: next.tag || prev.tag,
    }));
  }, [editorOpen, editingId, detailQ.data]);

  const createMut = useCreateProviderTemplateMutation();
  const updateMut = useUpdateProviderTemplateMutation();
  const deleteMut = useDeleteProviderTemplateMutation();
  const testMut = useTestProviderTemplateMutation();

  const health = healthQ.data?.providers?.[0];
  const proxyUnavailable = !!healthQ.data?.unavailable;
  const senders = sendersQ.data?.senders ?? [];
  const templates = templatesQ.data?.templates ?? [];

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setShowPreview(false);
    setEditorOpen(true);
  };

  const openEdit = (tpl: ProviderTemplate) => {
    setEditingId(tpl.id);
    setForm(formFromTemplate(tpl));
    setShowPreview(false);
    setEditorOpen(true);
  };

  const htmlAwaiting =
    editorOpen && editingId != null && !form.html_content && (detailQ.isPending || detailQ.isFetching);
  const htmlMissing =
    editorOpen && editingId != null && detailQ.isFetched && !detailQ.isError && !form.html_content;

  const selectedSender = senders.find(
    (s) => (s.email || '').toLowerCase() === form.sender.email.trim().toLowerCase(),
  );
  const senderUnverified =
    !!form.sender.email.trim() && selectedSender != null && !isVerified(selectedSender);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    if (!canWrite) return;
    const payload: CreateProviderTemplatePayload = {
      name: form.name.trim(),
      subject: form.subject.trim(),
      sender: { name: form.sender.name.trim(), email: form.sender.email.trim() },
      html_content: form.html_content,
      is_active: form.is_active !== false,
      ...(form.reply_to?.trim() ? { reply_to: form.reply_to.trim() } : {}),
      ...(form.tag?.trim() ? { tag: form.tag.trim() } : {}),
    };
    if (!payload.name || !payload.subject || !payload.sender.email) {
      toast.error('Name, subject, and sender email are required', { richColors: true });
      return;
    }
    if (editingId == null && !payload.html_content.trim()) {
      toast.error('HTML is required', { richColors: true });
      return;
    }
    if (editingId != null && htmlAwaiting) {
      toast.error('Wait for vendor HTML to load before saving', { richColors: true });
      return;
    }
    if (editingId != null && htmlMissing && !payload.html_content.trim()) {
      toast.error('No HTML loaded — paste HTML or cancel to avoid wiping the vendor template', {
        richColors: true,
      });
      return;
    }
    try {
      if (editingId == null) {
        const created = await createMut.mutateAsync({
          provider,
          body: payload,
          idempotencyKey: generateIdempotencyKey(),
        });
        toast.success('Provider template created', { richColors: true });
        setEditorOpen(false);
        setPostCreate({ id: created.id, name: created.name ?? payload.name });
      } else {
        await updateMut.mutateAsync({
          provider,
          id: editingId,
          body: payload,
          idempotencyKey: generateIdempotencyKey(),
        });
        toast.success('Provider template updated', { richColors: true });
        setEditorOpen(false);
      }
    } catch (error) {
      const info = describeProviderError(error);
      toast.error(info.title, { description: info.description, richColors: true });
    }
  };

  const handleDelete = async (tpl: ProviderTemplate) => {
    if (!canWrite) return;
    if (!window.confirm(`Delete provider template ${tpl.name || tpl.id}?`)) return;
    try {
      await deleteMut.mutateAsync({
        provider,
        id: tpl.id,
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success('Template deleted', { richColors: true });
    } catch (error) {
      const info = describeProviderError(error);
      toast.error(info.title, {
        description: info.description,
        richColors: true,
      });
    }
  };

  const handleTest = async (e: FormEvent) => {
    e.preventDefault();
    if (!canWrite || testId == null) return;
    const emails = testEmails
      .split(/[,;\s]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (!emails.length) {
      toast.error('Enter at least one email', { richColors: true });
      return;
    }
    try {
      await testMut.mutateAsync({
        provider,
        id: testId,
        body: emails.length === 1 ? { email: emails[0] } : { emails },
        idempotencyKey: generateIdempotencyKey(),
      });
      toast.success(`Test sent to ${emails.join(', ')}`, { richColors: true });
      setTestOpen(false);
    } catch (error) {
      const info = describeProviderError(error);
      toast.error(info.title, { description: info.description, richColors: true });
    }
  };

  if (permLoading) {
    return (
      <DeliveryPageShell loading icon={null} title="Provider" description="">
        {null}
      </DeliveryPageShell>
    );
  }

  if (!canView) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl">
          <DeliveryAccessDenied />
        </div>
      </div>
    );
  }

  const label = formatProviderName(provider);

  return (
    <DeliveryPageShell
      icon={<Radio className="h-4 w-4" />}
      title={label}
      description={
        row?.channel
          ? `${formatChannel(String(row.channel))} provider. Affordances follow capabilities, not the vendor name.`
          : 'Provider detail. Affordances follow capabilities, not the vendor name.'
      }
      actions={
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            providersQ.refetch();
            healthQ.refetch();
            templatesQ.refetch();
            sendersQ.refetch();
            if (activeTab === 'events') eventsQ.refetch();
          }}
          className="gap-2"
        >
          {templatesQ.isFetching || healthQ.isFetching ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </Button>
      }
    >
      <div className="flex flex-wrap items-center gap-4 rounded-xl border border-gray-100 bg-white px-4 py-3 shadow-sm">
        <div>
          <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Health</p>
          <HealthStatus
            state={resolveHealth(row, health, proxyUnavailable, healthQ.isLoading)}
          />
        </div>
        {row?.is_default ? (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700">
            Default for channel
          </span>
        ) : null}
        <div className="min-w-0 flex-1">
          <CapabilityChips caps={caps} />
        </div>
        {templateIdColumn ? (
          <p className="text-xs text-gray-500">
            Maps via <code className="rounded bg-gray-100 px-1">{templateIdColumn}</code>
          </p>
        ) : null}
      </div>

      {postCreate && (
        <div className="rounded-xl border border-blue-200 bg-blue-50 px-4 py-3">
          <p className="text-sm font-semibold text-blue-950">
            Template {postCreate.name || postCreate.id} created — it is not live yet
          </p>
          <p className="mt-1 text-xs text-blue-900/80">
            Test it, then map it onto a local notification_template row. Skipping the map leaves it
            inert — exactly the state that sat on eight giveaway types.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            {canTest && canWrite && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => {
                  setTestId(postCreate.id);
                  setTestEmails('');
                  setTestOpen(true);
                }}
                className="gap-2"
              >
                <Send className="h-3.5 w-3.5" />
                Test send
              </Button>
            )}
            {canWrite && (
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  setMapId(postCreate.id);
                  setMapOpen(true);
                }}
                className="gap-2"
              >
                <Link2 className="h-3.5 w-3.5" />
                Map to local row
              </Button>
            )}
            <Button type="button" size="sm" variant="ghost" onClick={() => setPostCreate(null)}>
              Dismiss
            </Button>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {availableTabs.map((id) => (
          <button
            key={id}
            type="button"
            onClick={() => setTab(id)}
            className={`rounded-md border px-3 py-1.5 text-sm capitalize transition-colors duration-150 ${
              activeTab === id
                ? 'border-blue-200 bg-blue-50 text-blue-800'
                : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
            }`}
          >
            {id}
          </button>
        ))}
      </div>

      {activeTab === 'templates' && canTemplates && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Provider templates</h2>
            {canWrite && (
              <Button type="button" size="sm" onClick={openCreate} className="gap-2">
                <Plus className="h-4 w-4" />
                New template
              </Button>
            )}
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            {templatesQ.isLoading ? (
              <div className="space-y-2 p-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            ) : templatesQ.isError ? (
              <p className="p-8 text-center text-sm text-red-600">
                {(() => {
                  const info = describeProviderError(templatesQ.error);
                  return `${info.title}: ${info.description}`;
                })()}
              </p>
            ) : templates.length === 0 ? (
              <p className="p-8 text-center text-sm text-gray-500">No provider templates.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">Template</th>
                      <th className="px-4 py-3 font-medium">Subject</th>
                      <th className="px-4 py-3 font-medium">Mapped to</th>
                      <th className="px-4 py-3 font-medium" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {templates.map((tpl) => {
                      const mapped = tpl.mapped_to ?? [];
                      const orphan = tpl.orphan === true || mapped.length === 0;
                      return (
                        <tr key={String(tpl.id)}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-gray-900">{tpl.name || `Template ${tpl.id}`}</p>
                            <p className="font-mono text-[11px] text-gray-400">{tpl.id}</p>
                          </td>
                          <td className="max-w-xs truncate px-4 py-3 text-gray-600">
                            {tpl.subject || '—'}
                          </td>
                          <td className="px-4 py-3">
                            {orphan ? (
                              <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800">
                                Orphan
                              </span>
                            ) : (
                              <div className="flex flex-wrap gap-1">
                                {mapped.map((m, i) => (
                                  <span
                                    key={`${m.ntmplid ?? i}`}
                                    className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800"
                                  >
                                    {m.name_system || `#${m.ntmplid}`}
                                  </span>
                                ))}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => openEdit(tpl)}
                                className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                                {canWrite ? 'Edit' : 'View'}
                              </button>
                              {canWrite && canTest && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setTestId(tpl.id);
                                    setTestEmails('');
                                    setTestOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900"
                                >
                                  <Send className="h-3.5 w-3.5" />
                                  Test
                                </button>
                              )}
                              {canWrite && (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setMapId(tpl.id);
                                    setMapOpen(true);
                                  }}
                                  className="inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900"
                                >
                                  <Link2 className="h-3.5 w-3.5" />
                                  Map
                                </button>
                              )}
                              {canWrite && (
                                <button
                                  type="button"
                                  disabled={deleteMut.isPending}
                                  onClick={() => handleDelete(tpl)}
                                  className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Delete
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      {activeTab === 'senders' && canSenders && (
        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Verified senders</h2>
            <p className="text-xs text-gray-500">
              An unverified sender is accepted at create time and fails at delivery. Keep this list
              next to the template editor.
            </p>
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            {sendersQ.isLoading ? (
              <Skeleton className="h-24 w-full" />
            ) : sendersQ.isError ? (
              <p className="p-8 text-center text-sm text-red-600">
                {getApiErrorMessage(sendersQ.error, 'Failed to load senders.')}
              </p>
            ) : senders.length === 0 ? (
              <p className="p-8 text-center text-sm text-gray-500">No senders returned.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {senders.map((s, i) => (
                  <li key={`${s.email ?? i}`} className="flex items-center justify-between px-4 py-3">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{s.name || s.email || '—'}</p>
                      {s.name && s.email ? (
                        <p className="text-xs text-gray-500">{s.email}</p>
                      ) : null}
                    </div>
                    {isVerified(s) ? (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        Verified
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700">
                        <XCircle className="h-3.5 w-3.5" />
                        Unverified
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {activeTab === 'events' && canEvents && (
        <section className="space-y-3">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">Delivery events</h2>
            <p className="text-xs text-gray-500">
              Vendor-side “did it arrive”. The local delivery ledger only records what was dispatched.
            </p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
            <input
              value={eventEmail}
              onChange={(e) => setEventEmail(e.target.value)}
              placeholder="Email"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <select
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            >
              <option value="">Any event</option>
              {EVENT_NAMES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
            <input
              value={eventTemplateId}
              onChange={(e) => setEventTemplateId(e.target.value)}
              placeholder="Template id"
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={eventStart}
              onChange={(e) => setEventStart(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <input
              type="date"
              value={eventEnd}
              onChange={(e) => setEventEnd(e.target.value)}
              className="rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="overflow-hidden rounded-xl border border-gray-100 bg-white shadow-sm">
            {eventsQ.isLoading ? (
              <Skeleton className="h-32 w-full" />
            ) : eventsQ.isError ? (
              <p className="p-8 text-center text-sm text-red-600">
                {(() => {
                  const info = describeProviderError(eventsQ.error);
                  return `${info.title}: ${info.description}`;
                })()}
              </p>
            ) : (eventsQ.data?.events ?? []).length === 0 ? (
              <p className="p-8 text-center text-sm text-gray-500">No events for these filters.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full text-left text-sm">
                  <thead className="border-b bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-4 py-3 font-medium">When</th>
                      <th className="px-4 py-3 font-medium">Event</th>
                      <th className="px-4 py-3 font-medium">Email</th>
                      <th className="px-4 py-3 font-medium">Template</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {(eventsQ.data?.events ?? []).map((ev, i) => (
                      <tr key={`${ev.email}-${ev.event}-${ev.date ?? ev.ts}-${i}`}>
                        <td className="whitespace-nowrap px-4 py-3 text-xs text-gray-600">
                          {ev.date || ev.ts || '—'}
                        </td>
                        <td className="px-4 py-3">
                          <EventBadge event={String(ev.event || '')} />
                        </td>
                        <td className="px-4 py-3 text-gray-800">{ev.email || '—'}</td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-500">
                          {ev.template_id ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}

      <Dialog open={editorOpen} onOpenChange={setEditorOpen}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId == null ? 'New provider template' : `Edit template ${editingId}`}</DialogTitle>
            <DialogDescription>
              Vendor-side template. Mapping onto a local row is a separate step on the messaging
              surface.
            </DialogDescription>
          </DialogHeader>

          {htmlAwaiting ? (
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading vendor HTML…
            </div>
          ) : null}
          {htmlMissing ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Vendor returned no HTML for this template. Saving now would overwrite it with a blank
              body — paste HTML only if you intend to replace it.
            </div>
          ) : null}
          {editingId != null && detailQ.isError ? (
            <p className="text-sm text-red-600">
              {getApiErrorMessage(detailQ.error, 'Failed to load template HTML.')}
            </p>
          ) : null}

          {canSenders && senders.some((s) => !isVerified(s)) && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              Unverified senders are accepted here and fail at delivery. Prefer a verified address.
            </div>
          )}

          <form onSubmit={handleSave} className="space-y-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <label className="text-xs font-medium text-gray-600">
                Name
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  disabled={!canWrite}
                />
              </label>
              <label className="text-xs font-medium text-gray-600">
                Subject
                <input
                  required
                  value={form.subject}
                  onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  disabled={!canWrite}
                />
              </label>
              <label className="text-xs font-medium text-gray-600">
                Sender name
                <input
                  value={form.sender.name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, sender: { ...f.sender, name: e.target.value } }))
                  }
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  disabled={!canWrite}
                />
              </label>
              <label className="text-xs font-medium text-gray-600">
                Sender email
                {senders.length > 0 ? (
                  <select
                    value={form.sender.email}
                    onChange={(e) => {
                      const email = e.target.value;
                      const match = senders.find((s) => s.email === email);
                      setForm((f) => ({
                        ...f,
                        sender: { name: match?.name || f.sender.name, email },
                      }));
                    }}
                    className={`mt-1 w-full rounded-md border px-3 py-2 text-sm ${
                      senderUnverified ? 'border-amber-400' : 'border-gray-300'
                    }`}
                    disabled={!canWrite}
                  >
                    <option value="">Select sender…</option>
                    {senders.map((s) => (
                      <option key={s.email} value={s.email ?? ''}>
                        {s.email}
                        {isVerified(s) ? '' : ' (unverified)'}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    required
                    type="email"
                    value={form.sender.email}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, sender: { ...f.sender, email: e.target.value } }))
                    }
                    className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                    disabled={!canWrite}
                  />
                )}
              </label>
              <label className="text-xs font-medium text-gray-600">
                Reply-to
                <input
                  type="email"
                  value={form.reply_to ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, reply_to: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  disabled={!canWrite}
                />
              </label>
              <label className="text-xs font-medium text-gray-600">
                Tag
                <input
                  value={form.tag ?? ''}
                  onChange={(e) => setForm((f) => ({ ...f, tag: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                  disabled={!canWrite}
                />
              </label>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={form.is_active !== false}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))}
                disabled={!canWrite}
              />
              Active
            </label>
            <div>
              <div className="mb-1 flex items-center justify-between">
                <span className="text-xs font-medium text-gray-600">HTML</span>
                <button
                  type="button"
                  onClick={() => setShowPreview((v) => !v)}
                  className="text-xs text-blue-700 hover:text-blue-900"
                >
                  {showPreview ? 'Edit' : 'Preview'}
                </button>
              </div>
              {htmlAwaiting ? (
                <div className="flex h-[210px] items-center justify-center rounded-md border border-dashed border-gray-300 bg-gray-50 text-sm text-gray-500">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Fetching HTML from vendor…
                </div>
              ) : showPreview ? (
                <iframe
                  title="Template preview"
                  sandbox=""
                  srcDoc={form.html_content}
                  className="h-64 w-full rounded-md border border-gray-200 bg-white"
                />
              ) : (
                <textarea
                  required={editingId == null}
                  value={form.html_content}
                  onChange={(e) => setForm((f) => ({ ...f, html_content: e.target.value }))}
                  rows={12}
                  placeholder={editingId == null ? 'HTML body' : undefined}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 font-mono text-xs"
                  disabled={!canWrite}
                />
              )}
            </div>
            {canWrite && (
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditorOpen(false)}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={createMut.isPending || updateMut.isPending || htmlAwaiting}
                  className="gap-2"
                >
                  {(createMut.isPending || updateMut.isPending) && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  {editingId == null ? 'Create' : 'Save'}
                </Button>
              </DialogFooter>
            )}
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Test send</DialogTitle>
            <DialogDescription>
              Send this vendor template to yourself before mapping it live.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleTest} className="space-y-3">
            <input
              type="text"
              required
              value={testEmails}
              onChange={(e) => setTestEmails(e.target.value)}
              placeholder="you@company.com, other@company.com"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
            />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setTestOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={testMut.isPending} className="gap-2">
                {testMut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Send test
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <MapLocalTemplateDialog
        open={mapOpen}
        onOpenChange={setMapOpen}
        providerLabel={label}
        templateId={mapId}
        templateIdColumn={templateIdColumn}
      />
    </DeliveryPageShell>
  );
};

const EventBadge = ({ event }: { event: string }) => {
  const tone =
    event === 'delivered'
      ? 'bg-emerald-50 text-emerald-700'
      : event === 'bounced' || event === 'blocked' || event === 'spam'
        ? 'bg-red-50 text-red-700'
        : 'bg-amber-50 text-amber-800';
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}>{event || '—'}</span>
  );
};

export default DeliveryProviderDetailPage;
